import * as Highcharts from "highcharts";
import * as React from "react";
import HighchartsReact from "highcharts-react-official";
import {
  ICourseComponentPart,
  IExerciseAnalyticsCommonProps,
  IUnitInstanceAnalytics,
  IUnitsData,
  IUnitsDataBase,
  OnSelectAssessmentFn,
  UnitChartType,
  UnitsCategory,
  IUnitsChartData,
} from "../../../../../App/Analytics/Common";
import { FilterCount } from "../../FilterCriteria/FilterCriteria";
import {
  IUnitInstanceAnalyticsGraphData,
  createGradient,
  createUnitGraphSeries,
  defaultBarTooltip,
  generateUnitsChartData,
} from "../../../../../formatters/analytics";
import {
  calculateUnitInstanceAnalyticsGraphData,
  getUnitAnalytics,
  IUnitInstanceStatistics,
  unitsInstanceAnalyticsFromPartInstanceSummary,
} from "../../../../../parsers/analytics";

import Spinner from "../../../../utils/Spinner";
import colors from "../../../../../styles/colors";

import {
  defaultStyle,
  defaultTooltip,
  defaultAxisLabels,
  centerHorizontalBarTooltip,
} from "../../../../../utils/charts";
import { useEffect, useState } from "react";
import { UnitContainer } from "../UnitsAnalytics.styles";
import {
  FilterCountContainer,
  FilteredIndividualQuestionChartContainer,
  IndividualQuestionChartContainer,
  QuestionChartGridContainer,
  QuestionName,
  QuestionNameContainer,
} from "./UnitInstanceAnalytics.styles";

interface IUnitInstanceAnalyticsProps extends IExerciseAnalyticsCommonProps {
  courseData: ICourseComponentPart[];
  type?: UnitChartType;
}

interface IUnitInstanceAnalyticsState {
  loading: boolean;
  failed: boolean;
  filteredGraphData: IUnitInstanceAnalyticsGraphData[];
  filteredTotalResponses?: number;
  totalResponses?: number;
  graphData: IUnitInstanceAnalyticsGraphData[];
  unitsInstanceAnalytics?: IUnitInstanceAnalytics[];
}

interface IUnitQuestionAnalyticsGraph {
  chartOptions: Highcharts.Options;
}

interface IUnitInstanceChartProps {
  data: IUnitsData;
  filteredData: IUnitsData;
  onSelectAssessmentName: OnSelectAssessmentFn | undefined;
  type?: UnitChartType;
  isLast: boolean;
  isFilterActive: boolean;
  unitInstanceAnalytics: IUnitInstanceAnalytics | undefined;
}

export const questionOptions = (
  data: IUnitsChartData,
  isLast: boolean,
  type?: UnitChartType,
  isFilterActive?: boolean
): Highcharts.Options => {
  const seriesData = [data[UnitsCategory.Correct], data[UnitsCategory.Incorrect], data[UnitsCategory.DidntKnow]];
  const extraWidthInstruction = isFilterActive ? 0 : 20;
  const widthInstructionOrDefault = type === UnitChartType.Instruction ? 25 + extraWidthInstruction : 17;
  return {
    ...defaultStyle,
    chart: {
      backgroundColor: "transparent",
      /* align bars with name */
      marginBottom: isLast ? undefined : 0,
      marginTop: isLast ? undefined : 22,
      type: "bar",
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: isLast,
      itemStyle: {
        fontSize: "1.4rem",
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 5,
        pointWidth: widthInstructionOrDefault,
      },
      series: {
        dataLabels: {
          style: {
            fontSize: "1.4rem",
          },
        },
        stacking: "normal",
      },
    },
    series: seriesData.map(createUnitGraphSeries) as Highcharts.SeriesBarOptions[],
    title: {
      text: undefined,
    },
    tooltip: {
      formatter() {
        const tooltipAmount = data[this.series.name].amount;
        return defaultBarTooltip(this.color, this.series.name, this.y!, tooltipAmount[this.key as string]);
      },
      positioner(...params) {
        return centerHorizontalBarTooltip(this.chart)(...params);
      },
      ...defaultTooltip,
    },
    xAxis: {
      gridLineColor: "transparent",
      labels: {
        enabled: false,
      },
      lineColor: "transparent",
      visible: false,
    },
    yAxis: {
      gridLineColor: "transparent",
      lineColor: "transparent",
      max: 100,
      min: 0,
      title: {
        style: {
          fontSize: "1.45rem",
        },
        text: "",
      },
      ...defaultAxisLabels(),
      visible: isLast,
    },
  };
};

export const filteredQuestionOptions = (
  data: IUnitsChartData,
  isLast: boolean,
  type?: UnitChartType,
  isFilterActive?: boolean
): Highcharts.Options => {
  return {
    ...questionOptions(data, isLast, type, isFilterActive),
    chart: {
      /* tslint:disable-next-line */
      backgroundColor: "transparent",
      events: {
        load() {
          this.series.forEach(s =>
            s.data.forEach(p => {
              if (p.category === 0) {
                p.update({
                  color: createGradient(colors.chartFilteredGray70, colors.chartFilteredGray100),
                });
              }
            })
          );
        },
      },
    },
    marginBottom: isLast ? undefined : 0,
    marginTop: isLast ? undefined : 22,
  } as Highcharts.Options;
};

export const UnitQuestionAnalyticsGraph: React.SFC<IUnitQuestionAnalyticsGraph> = ({ chartOptions }) => {
  return (
    <HighchartsReact
      containerProps={{ style: { width: "100%", height: "100%", display: "flex", alignContent: "center" } }}
      highcharts={Highcharts}
      options={chartOptions}
    />
  );
};

const calculateAllUsers = (unitData: IUnitsData): number =>
  [unitData.Correct, unitData.Incorrect, unitData["Didn't Know"]].reduce((acc, curr) => acc + curr.amount, 0);

export const UnitInstanceChart: React.FC<IUnitInstanceChartProps> = ({
  data,
  filteredData,
  onSelectAssessmentName,
  unitInstanceAnalytics,
  isLast,
  isFilterActive,
  type,
}) => {
  const handleClick = () => {
    if (onSelectAssessmentName && unitInstanceAnalytics) {
      return onSelectAssessmentName(data.name, data.identifier, type!, unitInstanceAnalytics);
    }
    return;
  };

  const allUsersCount = calculateAllUsers(data);
  const filteredUsersCount = calculateAllUsers(filteredData);
  const datum = generateUnitsChartData([data, filteredData] as IUnitsDataBase[]);

  return (
    <UnitContainer onClick={handleClick()} data-testid="question-analytics-chart">
      <QuestionChartGridContainer>
        <QuestionNameContainer isLast={isLast}>
          <QuestionName>{data.name}</QuestionName>
        </QuestionNameContainer>
        <FilterCountContainer isLast={isLast}>
          <FilterCount
            allUsersCount={allUsersCount}
            selectedCount={filteredUsersCount}
            inFilterCard={!isFilterActive}
          />
        </FilterCountContainer>
        {isFilterActive ? (
          <FilteredIndividualQuestionChartContainer isLast={isLast} data-testid="filtered-question-chart">
            <UnitQuestionAnalyticsGraph chartOptions={filteredQuestionOptions(datum, isLast, type)} />
          </FilteredIndividualQuestionChartContainer>
        ) : (
          <IndividualQuestionChartContainer isLast={isLast} data-testid="unfiltered-question-chart">
            <UnitQuestionAnalyticsGraph chartOptions={questionOptions(datum, isLast, type)} />
          </IndividualQuestionChartContainer>
        )}
      </QuestionChartGridContainer>
    </UnitContainer>
  );
};

const IndividualAnalytics: React.FC<IUnitInstanceAnalyticsProps> = ({
  courseData,
  fetchAnalytics,
  onSelectAssessmentName,
  searchLocale,
  selectedUsers,
  users,
  usersAnalytics,
  type,
}) => {
  const [analyticsState, setAnalyticsState] = useState<IUnitInstanceAnalyticsState>({
    failed: false,
    filteredGraphData: [],
    filteredTotalResponses: 0,
    graphData: [],
    loading: true,
    totalResponses: 0,
    unitsInstanceAnalytics: [],
  });
  useEffect(() => {
    const fetchData = async () => {
      const calculatedDataList: IUnitInstanceStatistics[] = [];
      const unitsInstanceAnalyticsList = [];

      if (fetchAnalytics) {
        for (const e of courseData) {
          const [error, response] = await fetchAnalytics(e.index);
          if (error !== null) {
            setAnalyticsState({ ...analyticsState, failed: true, loading: false });
            return null;
          }
          const unitsInstanceAnalytics = unitsInstanceAnalyticsFromPartInstanceSummary(response.data[1]);
          const calculatedData = calculateUnitInstanceAnalyticsGraphData(
            e.name,
            e.index,
            unitsInstanceAnalytics,
            users,
            usersAnalytics,
            selectedUsers,
            searchLocale
          );
          calculatedDataList.push(calculatedData);
          unitsInstanceAnalyticsList.push(unitsInstanceAnalytics);
        }
        return { calculatedDataList, unitsInstanceAnalyticsList };
      }
      return null;
    };

    fetchData().then(data => {
      if (data === null || data === undefined) {
        setAnalyticsState({ ...analyticsState, failed: true, loading: false });
      }

      setAnalyticsState({
        ...analyticsState,
        failed: false,
        filteredGraphData: data?.calculatedDataList?.map(d => d.filteredGraphData)!,
        graphData: data?.calculatedDataList?.map(d => d.graphData)!,
        loading: false,
        unitsInstanceAnalytics: data?.unitsInstanceAnalyticsList,
      });
    });
  }, [fetchAnalytics, courseData, users, usersAnalytics, selectedUsers, searchLocale]);

  const { failed, loading } = analyticsState;
  if (loading) {
    return <Spinner style={{ margin: 0 }} />;
  }
  if (failed) {
    return <>Failed to retrieve analytics.</>;
  }

  const datum = analyticsState.graphData.map((elem, index) =>
    getUnitAnalytics(elem, `${type}${index + 1} ${elem.name}`)
  );
  const filteredDatum = analyticsState.filteredGraphData.map((elem, index) =>
    getUnitAnalytics(elem, `${type}${index + 1} ${elem.name}`)
  );

  const isFilterActive = selectedUsers.size > 0 || searchLocale !== undefined;

  const unitAnalytics = analyticsState.unitsInstanceAnalytics
    ? analyticsState.unitsInstanceAnalytics
    : Array(datum.length);

  return (
    <>
      {datum.map((dataPoint, idx: number) => (
        <UnitInstanceChart
          data={dataPoint}
          filteredData={filteredDatum[idx]}
          isFilterActive={isFilterActive}
          isLast={idx === datum.length - 1}
          key={dataPoint.name}
          onSelectAssessmentName={onSelectAssessmentName}
          type={type}
          unitInstanceAnalytics={unitAnalytics[idx]}
        />
      ))}
    </>
  );
};

export default IndividualAnalytics;
